/**
 * The engine: state construction, the phase machine, and sealed-bid resolution.
 *
 * `applyAction` is the only way state ever changes. Given the same `settings.seed` and
 * the same action sequence, two runs produce byte-identical states — that property is
 * what makes the tests, the balance harness and the AI's lookahead all possible, so
 * every random draw goes through `withRng`, which threads `state.rngState` in and out.
 */

import {
  ALL_SEATS, CONTEST_DEFENDER_BONUS, CONTEST_FLEET_STRENGTH, CONTEST_LOSER_FLEET_LOSS,
  DEFAULT_ROUND_SECONDS, DEFAULT_TOTAL_ROUNDS, PLAYER_COLORS, PLAYER_NAMES_DEFAULT,
  REGION_SCORING_ROUNDS, RESOURCE_CAP, RESOURCE_KINDS, STARTING_FLEETS,
  STARTING_POPULATION, STARTING_RESOURCES, TECH_MARKET_SIZE,
} from './constants';
import { ALL_CHARTER_IDS, CHARTERS } from './charters';
import { ALL_EVENT_IDS, applyEventImmediate, eventProductionBonus, eventUpkeepSurcharge } from './events';
import { ALL_TECH_IDS } from './tech';
import { generateGalaxy } from './map';
import { Rng } from './rng';
import { applyCommandAction, checkAction, log, refillTechMarket } from './actions';
import { computeTurnOrder, determineWinner, refreshScores, scoreRegions } from './scoring';
import {
  addResources, colonyCount, commandPointsFor, contestedSystems, fleetsAt, fleetsAtOwnedBy,
  playerOf, populationCap, productionFor, seatsPresentAt, systemOf, upkeepFor,
} from './queries';
import { neighborKeys, parseKey } from './hex';
import type {
  BidOutcome, BidRound, CharterId, ControllerKind, GameAction, GameSettings, GameState,
  HexKey, Legality, NoticeSink, Phase, Player, PlayerId, PlayerStats, Resources,
} from './types';

/* --------------------------------------------------------------- construction */

export interface SeatConfig {
  kind: ControllerKind;
  name?: string;
  gamepadIndex?: number | null;
}

export const DEFAULT_SETTINGS: GameSettings = {
  seed: 1,
  totalRounds: DEFAULT_TOTAL_ROUNDS,
  roundSeconds: DEFAULT_ROUND_SECONDS,
  blitz: false,
  aiDifficulty: 'standard',
};

function emptyStats(): PlayerStats {
  return {
    systemsSurveyed: 0, outpostsBuilt: 0, coloniesBuilt: 0, contestsWon: 0,
    contestsLost: 0, fuelBid: 0, dataBid: 0, actionsTaken: 0, terraformed: 0,
  };
}

/**
 * Build a fresh match.
 *
 * Always seats four charters — empty human seats become AI so the board is always full,
 * which keeps region majorities and the auctions meaningful with two players at the couch.
 */
export function createGame(
  settings: Partial<GameSettings> = {},
  seats: SeatConfig[] = [],
): GameState {
  const merged: GameSettings = { ...DEFAULT_SETTINGS, ...settings };
  const rng = new Rng(merged.seed);
  const galaxy = generateGalaxy(rng);

  const players: Player[] = ALL_SEATS.map((id) => {
    const config = seats[id] ?? { kind: 'ai' as ControllerKind };
    const kind: ControllerKind = config.kind === 'empty' ? 'ai' : config.kind;
    return {
      id,
      name: config.name ?? PLAYER_NAMES_DEFAULT[id]!,
      kind,
      gamepadIndex: config.gamepadIndex ?? null,
      color: PLAYER_COLORS[id]!,
      resources: { ...STARTING_RESOURCES },
      population: STARTING_POPULATION,
      cp: 0,
      passed: false,
      techs: [],
      charters: [],
      scoredCharters: [],
      relics: [],
      megastructure: null,
      vp: 0,
      stats: emptyStats(),
    };
  });

  const state: GameState = {
    settings: merged,
    rngState: rng.state,
    round: 0,
    phase: 'setup',
    systems: galaxy.systems,
    fleets: {},
    players,
    turnOrder: [...ALL_SEATS],
    activeIndex: 0,
    techMarket: [],
    techDeck: [],
    charterDeck: [],
    charterOffer: [],
    eventDeck: [],
    currentEvent: null,
    bidQueue: [],
    activeBid: null,
    scoredRegionRounds: [],
    regionAwards: [],
    log: [],
    winner: null,
    nextFleetSeq: 0,
  };

  // Homeworlds start as developed colonies with fleets in orbit.
  ALL_SEATS.forEach((seat) => {
    const home = galaxy.homeworlds[seat]!;
    const system = state.systems[home]!;
    system.owner = seat;
    system.development = 'colony';
    system.surveyed = true;
    for (let i = 0; i < STARTING_FLEETS; i++) {
      const id = `f${state.nextFleetSeq++}`;
      state.fleets[id] = { id, owner: seat, at: home, movedThisRound: 0 };
    }
  });

  withRng(state, (r) => {
    // Tech deck: shuffle within tier bands so tier I cards surface early and a tier III
    // cannot appear in round 1, when nobody could legally buy it anyway.
    const byTier = [1, 2, 3].map((tier) =>
      r.shuffled(ALL_TECH_IDS.filter((id) => tierOf(id) === tier)),
    );
    state.techDeck = byTier.flat();
    state.charterDeck = r.shuffled(ALL_CHARTER_IDS);
    state.eventDeck = r.shuffled(ALL_EVENT_IDS);
  });

  refillTechMarket(state);
  refreshScores(state);
  log(state, null, `Galaxy seeded. ${Object.keys(state.systems).length} systems charted.`);
  return state;
}

function tierOf(id: string): number {
  if (id.endsWith('III')) return 3;
  if (id.endsWith('II')) return 2;
  return 1;
}

/** Run `fn` with a PRNG cursor, writing the advanced state back. */
function withRng<T>(state: GameState, fn: (rng: Rng) => T): T {
  const rng = new Rng(state.rngState);
  const result = fn(rng);
  state.rngState = rng.state;
  return result;
}

/* ------------------------------------------------------------------- dispatch */

/**
 * The single entry point for mutating a match.
 *
 * Returns the legality verdict rather than throwing on player error, so the UI can turn
 * `{ ok: false, reason }` straight into a HUD message and a buzz.
 */
export function applyAction(
  state: GameState,
  action: GameAction,
  notice?: NoticeSink,
): Legality {
  if (state.phase === 'gameOver') return { ok: false, reason: 'The match is over.' };

  if (action.kind === 'advancePhase') {
    advancePhase(state, notice);
    return { ok: true };
  }

  if (action.kind === 'submitBid') {
    return submitBid(state, action.player, action.amount, notice);
  }

  const legality = checkAction(state, action);
  if (!legality.ok) {
    notice?.({ type: 'illegal', player: action.player, reason: legality.reason });
    return legality;
  }

  applyCommandAction(state, action, notice);
  refreshScores(state);
  advanceTurn(state, notice);
  return { ok: true };
}

/**
 * Hand the baton to the next seat still in the Command phase.
 *
 * Round-robin single actions are the reason this game has almost no downtime: a seat
 * acts, then everyone else acts once before it comes back around. When every seat has
 * passed or run out of Command Points, the phase ends on its own.
 */
function advanceTurn(state: GameState, notice?: NoticeSink): void {
  if (state.phase !== 'command') return;

  const stillIn = state.turnOrder.filter((seat) => !playerOf(state, seat).passed);
  if (stillIn.length === 0) {
    advancePhase(state, notice);
    return;
  }

  // Walk forward from the current seat until we land on someone who can still act.
  for (let step = 1; step <= state.turnOrder.length; step++) {
    const index = (state.activeIndex + step) % state.turnOrder.length;
    const seat = state.turnOrder[index]!;
    if (!playerOf(state, seat).passed) {
      state.activeIndex = index;
      return;
    }
  }
}

/** Whose single action it is, or `null` outside the Command phase. */
export function activeSeat(state: GameState): PlayerId | null {
  if (state.phase !== 'command') return null;
  return state.turnOrder[state.activeIndex] ?? null;
}

/* --------------------------------------------------------------- phase machine */

/**
 * Advance the phase machine one step.
 *
 * Each phase's *entry* work happens here, at the moment it becomes current, so the
 * renderer can always draw a consistent snapshot: by the time `phase === 'production'`
 * is observable, production has already been paid out.
 */
export function advancePhase(state: GameState, notice?: NoticeSink): void {
  switch (state.phase) {
    case 'setup':
      enterDrift(state, notice);
      break;

    case 'drift':
      enterProduction(state, notice);
      break;

    case 'production':
      enterCommand(state, notice);
      break;

    case 'command':
      enterContest(state, notice);
      break;

    case 'contest':
      // Each call resolves the revealed bid and pulls the next one, until the queue is
      // dry, at which point we fall through to the auction.
      if (!stepBidQueue(state, notice)) enterAuction(state, notice);
      break;

    case 'auction':
      if (!stepBidQueue(state, notice)) enterUpkeep(state, notice);
      break;

    case 'upkeep':
      if (state.round >= state.settings.totalRounds) enterFinalScoring(state, notice);
      else enterDrift(state, notice);
      break;

    case 'finalScoring':
      state.phase = 'gameOver';
      notice?.({ type: 'phase', phase: 'gameOver', round: state.round });
      notice?.({ type: 'gameOver', winner: state.winner });
      break;

    case 'gameOver':
      break;
  }
}

function setPhase(state: GameState, phase: Phase, notice?: NoticeSink): void {
  state.phase = phase;
  notice?.({ type: 'phase', phase, round: state.round });
}

/* ---------------------------------------------------------------------- drift */

function enterDrift(state: GameState, notice?: NoticeSink): void {
  state.round += 1;
  setPhase(state, 'drift', notice);

  // Clear last round's blocks that have expired, and reset per-round fleet mileage.
  for (const fleet of Object.values(state.fleets)) fleet.movedThisRound = 0;

  // Turn order is recomputed from standings before the event fires, so a card that
  // punishes "the leader" and the seat order agree on who that is.
  state.turnOrder = computeTurnOrder(state);
  state.activeIndex = 0;

  const eventId = withRng(state, (rng) => {
    if (state.eventDeck.length === 0) state.eventDeck = rng.shuffled(ALL_EVENT_IDS);
    return state.eventDeck.shift()!;
  });
  state.currentEvent = eventId;

  withRng(state, (rng) => applyEventImmediate(state, eventId, rng, notice));
  refreshScores(state);
  log(state, null, `Round ${state.round} — drift event: ${eventId}.`);
}

/* ----------------------------------------------------------------- production */

function enterProduction(state: GameState, notice?: NoticeSink): void {
  setPhase(state, 'production', notice);

  for (const seat of ALL_SEATS) {
    const player = playerOf(state, seat);
    const gained = addResources(productionFor(state, seat), eventProductionBonus(state, seat));

    player.resources = addResources(player.resources, gained);
    notice?.({ type: 'production', player: seat, gained });

    growPopulation(state, player);
    payUpkeep(state, player);
  }
  refreshScores(state);
}

/**
 * Population growth: half your colonies, rounded up, capped by the population track.
 * Colonies are the only thing that grows population, which is what makes `upgrade` the
 * spine of the economy rather than an optional extra.
 */
function growPopulation(state: GameState, player: Player): void {
  const colonies = colonyCount(state, player.id);
  if (colonies === 0) return;
  const growth = Math.ceil(colonies / 2);
  player.population = Math.min(populationCap(player), player.population + growth);
}

/**
 * Biomass upkeep, charged on even rounds. Short by `n` biomass costs `ceil(n / 2)`
 * population — a real spiral if you over-expand, but a survivable one.
 */
function payUpkeep(state: GameState, player: Player): void {
  if (state.round % 2 !== 0) return;

  const owed = upkeepFor(state, player.id) + eventUpkeepSurcharge(state);
  if (owed <= 0) return;

  const paid = Math.min(owed, player.resources.biomass);
  player.resources.biomass -= paid;

  const shortfall = owed - paid;
  if (shortfall > 0) {
    const starved = Math.ceil(shortfall / 2);
    player.population = Math.max(0, player.population - starved);
    log(state, player.id, `starves: ${shortfall} Biomass short, −${starved} Population.`);
  }
}

/* -------------------------------------------------------------------- command */

function enterCommand(state: GameState, notice?: NoticeSink): void {
  setPhase(state, 'command', notice);

  for (const seat of ALL_SEATS) {
    const player = playerOf(state, seat);
    player.cp = commandPointsFor(state, seat);
    player.passed = false;
  }
  state.activeIndex = 0;

  // A seat with zero CP (possible only via a pathological event chain) must not stall
  // the round-robin, so skip straight past anyone who cannot act.
  for (const seat of state.turnOrder) {
    if (playerOf(state, seat).cp <= 0) playerOf(state, seat).passed = true;
  }
  if (state.turnOrder.every((seat) => playerOf(state, seat).passed)) {
    enterContest(state, notice);
    return;
  }

  // Start the lap *at* index 0, not after it. `advanceTurn` walks forward from
  // `activeIndex + 1`, so calling it unconditionally here handed the baton to the second
  // seat and pushed the first seat to the end of the opening lap — which silently inverted
  // the catch-up rule, since turn order is ascending VP and index 0 is the trailing seat.
  if (playerOf(state, state.turnOrder[0]!).passed) advanceTurn(state, notice);
}

/* -------------------------------------------------------------------- contest */

function enterContest(state: GameState, notice?: NoticeSink): void {
  setPhase(state, 'contest', notice);

  state.bidQueue = contestedSystems(state).map((systemId) => ({
    kind: 'contest' as const,
    resource: 'fuel' as const,
    systemId,
    charterId: null,
    participants: seatsPresentAt(state, systemId),
    bids: {},
    revealed: false,
    outcome: null,
  }));
  state.activeBid = null;

  if (!pullNextBid(state)) enterAuction(state, notice);
}

/* -------------------------------------------------------------------- auction */

function enterAuction(state: GameState, notice?: NoticeSink): void {
  // Charters come up on odd rounds only, so the auction is an event rather than a chore.
  if (state.round % 2 === 0) {
    state.charterOffer = [];
    enterUpkeep(state, notice);
    return;
  }

  setPhase(state, 'auction', notice);

  const offer: CharterId[] = [];
  for (let i = 0; i < 2 && state.charterDeck.length > 0; i++) {
    offer.push(state.charterDeck.shift()!);
  }
  state.charterOffer = offer;

  state.bidQueue = offer.map((charterId) => ({
    kind: 'auction' as const,
    resource: 'data' as const,
    systemId: null,
    charterId,
    // Everyone may bid, including zero. A zero bid still competes on turn order, so a
    // charter never goes unclaimed while somebody could use it.
    participants: [...ALL_SEATS],
    bids: {},
    revealed: false,
    outcome: null,
  }));
  state.activeBid = null;

  if (!pullNextBid(state)) enterUpkeep(state, notice);
}

/* --------------------------------------------------------------------- upkeep */

function enterUpkeep(state: GameState, notice?: NoticeSink): void {
  setPhase(state, 'upkeep', notice);

  // Trim over-cap resources. The cap exists to stop hoarding, so this is deliberately
  // loud in the log rather than silent.
  for (const player of state.players) {
    for (const kind of RESOURCE_KINDS) {
      const excess = player.resources[kind] - RESOURCE_CAP;
      if (excess > 0) {
        player.resources[kind] = RESOURCE_CAP;
        log(state, player.id, `loses ${excess} ${kind} over the ${RESOURCE_CAP} cap.`);
      }
    }
  }

  if (REGION_SCORING_ROUNDS.includes(state.round)) {
    scoreRegions(state);
    log(state, null, `Region majorities scored for round ${state.round}.`);
  }

  state.currentEvent = null;
  refreshScores(state);
}

/* -------------------------------------------------------------- final scoring */

function enterFinalScoring(state: GameState, notice?: NoticeSink): void {
  setPhase(state, 'finalScoring', notice);
  refreshScores(state);

  for (const player of state.players) {
    for (const id of player.charters) {
      if (CHARTERS[id].test(state, player) && !player.scoredCharters.includes(id)) {
        player.scoredCharters.push(id);
      }
    }
  }

  state.winner = determineWinner(state);
  refreshScores(state);

  const summary = state.players
    .map((p) => `${p.name} ${p.vp}`)
    .join(' · ');
  log(state, null, `Final: ${summary}`);
}

/* ------------------------------------------------------------------ bid queue */

/** Move the next queued bid into `activeBid`. Returns false when the queue is empty. */
function pullNextBid(state: GameState): boolean {
  const next = state.bidQueue.shift();
  if (!next) {
    state.activeBid = null;
    return false;
  }
  state.activeBid = next;

  // Participants who cannot pay anything are auto-entered at zero, so a contest never
  // waits on a seat with no fuel.
  for (const seat of next.participants) {
    if (playerOf(state, seat).resources[next.resource] <= 0) next.bids[seat] = 0;
  }
  maybeReveal(state);
  return true;
}

/**
 * Resolve the revealed bid, then pull the next. Returns false when nothing is left,
 * telling the phase machine to move on.
 */
function stepBidQueue(state: GameState, notice?: NoticeSink): boolean {
  const bid = state.activeBid;
  if (bid && bid.revealed && bid.outcome === null) {
    resolveBid(state, bid, notice);
    return true;
  }
  if (bid && !bid.revealed) {
    // Still waiting on sealed bids — do not advance.
    return true;
  }
  return pullNextBid(state);
}

/** Record a sealed bid. Reveals automatically once everyone has locked in. */
export function submitBid(
  state: GameState,
  seat: PlayerId,
  amount: number,
  notice?: NoticeSink,
): Legality {
  const legality = checkAction(state, { kind: 'submitBid', player: seat, amount });
  if (!legality.ok) {
    notice?.({ type: 'illegal', player: seat, reason: legality.reason });
    return legality;
  }

  state.activeBid!.bids[seat] = amount;
  notice?.({ type: 'bidLocked', player: seat });
  maybeReveal(state, notice);
  return { ok: true };
}

/** Seats in the active bid who have not locked in yet. */
export function pendingBidders(state: GameState): PlayerId[] {
  const bid = state.activeBid;
  if (!bid || bid.revealed) return [];
  return bid.participants.filter((seat) => bid.bids[seat] === undefined);
}

function maybeReveal(state: GameState, notice?: NoticeSink): void {
  const bid = state.activeBid;
  if (!bid || bid.revealed) return;
  if (bid.participants.some((seat) => bid.bids[seat] === undefined)) return;
  bid.revealed = true;
  if (notice) notice({ type: 'phase', phase: state.phase, round: state.round });
}

/* -------------------------------------------------------------- bid resolution */

function resolveBid(state: GameState, bid: BidRound, notice?: NoticeSink): void {
  const outcome = bid.kind === 'contest'
    ? resolveContest(state, bid)
    : resolveAuction(state, bid);

  bid.outcome = outcome;
  notice?.({ type: 'bidReveal', outcome });
  refreshScores(state);
}

/**
 * Contest: `fleets × 2 + fuel bid + 1 if you own the system`.
 *
 * Fuel is spent whether you win or lose, which is what makes a contest a genuine bluff
 * rather than a formality — overbidding to hold a hex you did not need is how players
 * lose the round after next.
 */
function resolveContest(state: GameState, bid: BidRound): BidOutcome {
  const systemId = bid.systemId!;
  const system = systemOf(state, systemId);
  const strengths: Partial<Record<PlayerId, number>> = {};

  for (const seat of bid.participants) {
    const player = playerOf(state, seat);
    const amount = Math.min(bid.bids[seat] ?? 0, player.resources.fuel);

    player.resources.fuel -= amount;
    player.stats.fuelBid += amount;

    const fleets = fleetsAtOwnedBy(state, systemId, seat).length;
    const defender = system && system.owner === seat ? CONTEST_DEFENDER_BONUS : 0;
    strengths[seat] = fleets * CONTEST_FLEET_STRENGTH + amount + defender;
  }

  const winner = pickWinner(state, bid.participants, strengths, system?.owner ?? null);
  const losers = bid.participants.filter((seat) => seat !== winner);

  for (const seat of losers) {
    playerOf(state, seat).stats.contestsLost++;
    retreatFrom(state, systemId, seat);
  }
  if (winner !== null) playerOf(state, winner).stats.contestsWon++;

  const note = winner === null
    ? `Stalemate over ${systemId}.`
    : `${playerOf(state, winner).name} holds ${systemId}.`;
  log(state, winner, note);

  return { winner, strengths, losers, note };
}

/**
 * First-price sealed auction: highest Data bid takes the charter and pays it.
 * Losers pay nothing — the auction is meant to be a tempting gamble, not a tax.
 */
function resolveAuction(state: GameState, bid: BidRound): BidOutcome {
  const strengths: Partial<Record<PlayerId, number>> = {};
  for (const seat of bid.participants) {
    const player = playerOf(state, seat);
    strengths[seat] = Math.min(bid.bids[seat] ?? 0, player.resources.data);
  }

  const winner = pickWinner(state, bid.participants, strengths, null);
  const charterId = bid.charterId!;

  if (winner !== null) {
    const player = playerOf(state, winner);
    const paid = strengths[winner] ?? 0;
    player.resources.data -= paid;
    player.stats.dataBid += paid;
    player.charters.push(charterId);
    log(state, winner, `wins the ${CHARTERS[charterId].name} charter for ${paid} Data.`);
  } else {
    log(state, null, `The ${CHARTERS[charterId].name} charter goes unclaimed.`);
  }

  return {
    winner,
    strengths,
    losers: bid.participants.filter((seat) => seat !== winner),
    note: winner === null
      ? `${CHARTERS[charterId].name} unclaimed.`
      : `${playerOf(state, winner).name} takes ${CHARTERS[charterId].name}.`,
  };
}

/**
 * Highest strength wins. Ties go to the system's owner if they are tied for first,
 * otherwise to whoever sits earliest in this round's turn order — which is the trailing
 * seat, so ties quietly help the player who is behind.
 */
function pickWinner(
  state: GameState,
  participants: readonly PlayerId[],
  strengths: Partial<Record<PlayerId, number>>,
  owner: PlayerId | null,
): PlayerId | null {
  let best = -Infinity;
  for (const seat of participants) best = Math.max(best, strengths[seat] ?? 0);
  const tied = participants.filter((seat) => (strengths[seat] ?? 0) === best);

  if (tied.length === 1) return tied[0]!;
  if (owner !== null && tied.includes(owner)) return owner;

  for (const seat of state.turnOrder) {
    if (tied.includes(seat)) return seat;
  }
  return tied[0] ?? null;
}

/**
 * Losing a contest costs one fleet; the survivors fall back to the nearest system that
 * is yours or genuinely empty. If there is nowhere to go, the survivors are lost too —
 * being surrounded should hurt.
 */
function retreatFrom(state: GameState, systemId: HexKey, seat: PlayerId): void {
  const present = fleetsAtOwnedBy(state, systemId, seat);
  for (let i = 0; i < CONTEST_LOSER_FLEET_LOSS && i < present.length; i++) {
    delete state.fleets[present[i]!.id];
  }

  const survivors = fleetsAtOwnedBy(state, systemId, seat);
  if (survivors.length === 0) return;

  const haven = findHaven(state, systemId, seat);
  if (haven === null) {
    for (const fleet of survivors) delete state.fleets[fleet.id];
    log(state, seat, `is cut off at ${systemId} and loses the remaining fleets.`);
    return;
  }
  for (const fleet of survivors) fleet.at = haven;
  log(state, seat, `retreats from ${systemId} to ${haven}.`);
}

/** Breadth-first search for the nearest safe system to retreat into. */
function findHaven(state: GameState, from: HexKey, seat: PlayerId): HexKey | null {
  const seen = new Set<HexKey>([from]);
  let frontier: HexKey[] = [from];

  for (let depth = 0; depth < 6; depth++) {
    const next: HexKey[] = [];
    // Sort each frontier so the chosen haven is deterministic across replays.
    for (const current of frontier.slice().sort()) {
      for (const nk of neighborKeys(parseKey(current))) {
        if (seen.has(nk)) continue;
        seen.add(nk);
        const system = state.systems[nk];
        if (!system) continue;

        const mine = system.owner === seat;
        const contestedThere = fleetsAt(state, nk).some((f) => f.owner !== seat);
        if ((mine || system.owner === null) && !contestedThere) return nk;
        next.push(nk);
      }
    }
    if (next.length === 0) break;
    frontier = next;
  }
  return null;
}

/* -------------------------------------------------------------------- helpers */

/**
 * Drive the machine forward until it needs a human decision — i.e. until the Command
 * phase with a human to move, an unresolved sealed bid, or the game ending.
 *
 * The UI calls this after every automatic step so players never have to press A to walk
 * through production. `maxSteps` is a guard against a logic bug spinning forever.
 */
export function runUntilInput(state: GameState, notice?: NoticeSink, maxSteps = 200): void {
  for (let step = 0; step < maxSteps; step++) {
    if (state.phase === 'gameOver') return;

    if (state.phase === 'command') {
      const seat = activeSeat(state);
      // AI seats are stepped by the caller (they need their own think time), so stop
      // here either way and let the game loop decide.
      if (seat !== null) return;
    }

    if ((state.phase === 'contest' || state.phase === 'auction') && state.activeBid) {
      if (!state.activeBid.revealed) return;      // waiting on sealed bids
      if (state.activeBid.outcome === null) return; // waiting for the reveal animation
    }

    if (state.phase === 'finalScoring') return;

    advancePhase(state, notice);
  }
}

/** Total resources a seat is holding — handy for the HUD and the AI's heuristics. */
export function bankOf(state: GameState, seat: PlayerId): Resources {
  return { ...playerOf(state, seat).resources };
}

/** Deep clone, for AI lookahead and for the undo buffer. */
export function cloneState(state: GameState): GameState {
  return structuredClone(state);
}

/** Is the tech market exhausted? Used by the HUD to grey out Research. */
export function techMarketExhausted(state: GameState): boolean {
  return state.techMarket.length === 0 && state.techDeck.length === 0;
}

export { TECH_MARKET_SIZE };
